import React, { useState, useEffect } from 'react';
import { Card, List, Button, Input, Modal, Form, message, Tag, Typography, Space, Divider } from 'antd';
import { MessageOutlined, PlusOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';

const { Title, Paragraph, Text } = Typography;

const Forum = () => {
    const { user } = useAuth();
    const [forums, setForums] = useState([]);
    const [selectedForum, setSelectedForum] = useState(null);
    const [isPostModalVisible, setIsPostModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const [replyingTo, setReplyingTo] = useState(null);
    const [commentContent, setCommentContent] = useState('');

    const fetchForums = async () => {
    setLoading(true);
    try {
        const res = await api.get('/forums');
        if (res.data && res.data.length > 0) {
            setForums(res.data);
            // Priority: Find the forum matching user's role
            const matchedForum = res.data.find(f => f.access_role === user?.role) || res.data[0];
            setSelectedForum(matchedForum);
        } else {
            message.warning('No forums have been created by the Admin yet.');
        }
    } catch (e) {
        message.error('Failed to load forums');
    } finally {
        setLoading(false);
    }
};

    useEffect(() => { fetchForums(); }, []);

    const handleCreatePost = async (values) => {
    // 1. Safety Check: Ensure a forum is actually selected
    if (!selectedForum?.id) {
        message.error('No forum selected. Please wait for the page to load.');
        return;
    }

    setLoading(true);
    try {
        const payload = { 
            ...values, 
            forum_id: selectedForum.id 
        };
        const response = await api.post('/posts', payload);
        message.success('Post created successfully!');
        setIsPostModalVisible(false);
        form.resetFields();
        
        // Instead of just fetching everything, let's refresh the current forum view
        fetchForums(); 
    
    } catch (e) {
        // 2. Detailed Error Catching
        console.error("API Error Object:", e.response?.data);
        
        const errorMessage = e.response?.data?.message 
            || (e.response?.data?.errors ? Object.values(e.response.data.errors)[0][0] : 'Failed to create post');
            
        message.error(errorMessage);
    } finally {
        setLoading(false);
    }
    };

    const handleSendComment = async (postId) => {
        if (!commentContent.trim()) return;
        setLoading(true);
        try {
            await api.post(`/posts/${postId}/comments`, { content: commentContent });
            message.success('Comment added!');
            setCommentContent('');
            setReplyingTo(null);
            fetchForums(); // Refresh to see the new comment
        } catch (e) {
            message.error('Failed to post comment');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <Title level={2}>
                    <MessageOutlined style={{ color: '#FE1F08' }} /> {user?.role === 'mentor' ? 'Mentors' : 'Mentees'} Forum
                </Title>
                <Button 
                    type="primary" 
                    icon={<PlusOutlined />} 
                    onClick={() => setIsPostModalVisible(true)}
                    disabled={!selectedForum} // Disable if no forum is loaded
                    loading={loading}
                >
                    Start Discussion
                </Button>
            </div>

            <List
                dataSource={[...(selectedForum?.posts || [])].sort((a, b) => b.id - a.id)}
                renderItem={(post) => (
                    <Card style={{ marginBottom: 16 }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Title level={4} style={{ margin: 0 }}>{post.title}</Title>
                                <Tag color="red">{post.user?.name || 'Anonymous'}</Tag>
                            </div>
                            <Paragraph>{post.content}</Paragraph>
                            
                            {/* 3. Render existing comments */}
                            {post.comments?.length > 0 && (
                                <div style={{ background: '#f9f9f9', padding: '10px', borderRadius: '4px' }}>
                                    {post.comments.map(c => (
                                        <div key={c.id} style={{ marginBottom: 8 }}>
                                            <Text strong size="small">{c.user?.name}: </Text>
                                            <Text>{c.content}</Text>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <Divider style={{ margin: '12px 0' }} />
                            
                            {/* 4. Reply Logic UI */}
                            {replyingTo === post.id ? (
                                <Space.Compact style={{ width: '100%' }}>
                                    <Input 
                                        placeholder="Write a reply..." 
                                        value={commentContent}
                                        onChange={(e) => setCommentContent(e.target.value)}
                                    />
                                    <Button type="primary" onClick={() => handleSendComment(post.id)}>Send</Button>
                                    <Button onClick={() => setReplyingTo(null)}>Cancel</Button>
                                </Space.Compact>
                            ) : (
                                <Space>
                                    <Text type="secondary">{post.comments?.length || 0} Comments</Text>
                                    <Button 
                                        type="link" 
                                        size="small" 
                                        style={{ color: '#FE1F08' }}
                                        onClick={() => setReplyingTo(post.id)}
                                    >
                                        Reply
                                    </Button>
                                </Space>
                            )}
                        </Space>
                    </Card>
                )}
            />

            <Modal title="Start a Discussion" open={isPostModalVisible} onCancel={() => setIsPostModalVisible(false)} onOk={() => form.submit()}>
                <Form form={form} layout="vertical" onFinish={handleCreatePost}>
                    <Form.Item name="title" label="Topic Title" rules={[{ required: true }]}>
                        <Input placeholder="What's on your mind?" />
                    </Form.Item>
                    <Form.Item name="content" label="Description" rules={[{ required: true }]}>
                        <Input.TextArea rows={4} placeholder="Describe your topic in detail..." />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Forum;